import { createServerFn } from "@tanstack/react-start";
import type {
  CampaignStatisticsResponse,
  GetCampaignStatisticsApiWorkspacesWorkspaceIdCampaignsCampaignIdStatisticsGetParams,
} from "@/lib/api/generated";
import { getCampaignStatisticsApiWorkspacesWorkspaceIdCampaignsCampaignIdStatisticsGet } from "@/lib/api/generated/campaigns/campaigns";
import { logError, logInfo } from "@/lib/structured-log";

/**
 * 获取 Campaign 统计数据（服务器函数）
 * - 仅在服务端运行，自动转发认证信息（cookie/headers）
 * - 可选传入统计区间：start_date / end_date
 */
export const getCampaignStatisticsServer = createServerFn({ method: "GET" })
  .validator(
    (data: {
      workspaceId: string;
      campaignId: string;
      params?: GetCampaignStatisticsApiWorkspacesWorkspaceIdCampaignsCampaignIdStatisticsGetParams;
    }) => data,
  )
  .handler(async ({ data }): Promise<CampaignStatisticsResponse> => {
    logInfo("campaigns.statistics.get.server_fn_called", {
      workspace_id: data.workspaceId,
      campaign_id: data.campaignId,
      has_params: Boolean(data.params),
    });

    try {
      const response =
        await getCampaignStatisticsApiWorkspacesWorkspaceIdCampaignsCampaignIdStatisticsGet(
          data.workspaceId,
          data.campaignId,
          data.params,
        );

      if (response.status !== 200) {
        logError("campaigns.statistics.get.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          campaign_id: data.campaignId,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to get campaign statistics",
            status: response.status,
          }),
        );
      }

      logInfo("campaigns.statistics.get.success", {
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      return response.data;
    } catch (error) {
      logError("campaigns.statistics.get.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        campaign_id: data.campaignId,
      });
      throw error;
    }
  });
